import { fr } from "./fr";

type Plural = { readonly one: string; readonly other: string };

/** Catalogue d'une langue : toutes les clés du français, chaque valeur au même format. */
export type Messages = { readonly [K in keyof typeof fr]: (typeof fr)[K] extends string ? string : Plural };
export type MessageKey = keyof typeof fr;
export type MessageParams = Record<string, string | number>;

const catalogs: Record<string, Messages> = { fr };
let locale = "fr";
let messages: Messages = fr;

export function setLocale(next: string): void {
  const catalog = catalogs[next];
  if (!catalog) return;
  locale = next;
  messages = catalog;
}

export function currentLocale(): string {
  return locale;
}

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(count: number): "one" | "other" {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules.select(count) === "one" ? "one" : "other";
}

function interpolate(template: string, params: MessageParams | undefined): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : typeof value === "number" ? formatNumber(value) : value;
  });
}

/** Texte traduit, avec ses paramètres. Une clé au pluriel choisit sa forme selon `count`. */
export function t(key: MessageKey, params?: MessageParams): string {
  const message = messages[key] as string | Plural;
  if (typeof message === "string") return interpolate(message, params);
  const count = typeof params?.count === "number" ? params.count : 0;
  return interpolate(message[pluralCategory(count)], params);
}

/** Clé de texte qui existe : pour les clés construites (`module.${type}.name`). */
export function hasMessage(key: string): key is MessageKey {
  return key in messages;
}

const numberFormats = new Map<string, Intl.NumberFormat>();

/** Nombre dans la langue courante : « 12 345 ». */
export function formatNumber(value: number, maximumFractionDigits = 0): string {
  const cacheKey = `${locale}:${maximumFractionDigits}`;
  let format = numberFormats.get(cacheKey);
  if (!format) {
    format = new Intl.NumberFormat(locale, { maximumFractionDigits });
    numberFormats.set(cacheKey, format);
  }
  return format.format(value);
}

/** Durée lisible : « 45 s », « 3 min 05 s », « 2 h 10 min », « 3 jours ». */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return t("time.seconds", { s: String(seconds) });
  if (seconds < 3600) {
    return t("time.minutes", { m: String(Math.floor(seconds / 60)), s: String(seconds % 60).padStart(2, "0") });
  }
  if (seconds < 86_400) {
    return t("time.hours", { h: String(Math.floor(seconds / 3600)), m: String(Math.floor((seconds % 3600) / 60)) });
  }
  return t("time.days", { count: Math.floor(seconds / 86_400) });
}

/** Compte à rebours compact : « 3:05 », « 1:02:03 ». */
export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}

/** Proportion en dix-millièmes → « 45 % ». */
export function formatPermyriad(value: number): string {
  return `${formatNumber(Math.floor(value / 100))} %`;
}
